import { RUNNING_ASSESSMENT_STATUSES } from "@piiqtechnologies/ui-lib";
import { pendingNextRunAt, time } from "./pendingNextRun";
const isRunning = (report) => Boolean(report.status) && RUNNING_ASSESSMENT_STATUSES.includes(report.status);
/**
 * What the header should say about subtopic edits, from the point of view of the report on screen.
 *
 * The states are three points on one run: before it every report predates the edits and says so,
 * during it the wait is measured in minutes rather than in a date, and after it every report built
 * from the new subtopics says it carries them. Only the waiting is temporary. Reports older than
 * the edit get nothing — they were already correct for their own subtopics, and claiming otherwise
 * would put a notice on years of history.
 */
export const subtopicsBannerState = ({ nextRunAt, subtopicsUpdatedAt, lastRunAt, templateActive, autoReportEnabled, newTemplateNextRunAt, reports, selectedReportId, }) => {
    var _a;
    // Two ways to be waiting on a run, and they look the same to the reader: subtopics edited on the
    // template behind this report, or a whole new template created for the next one. The second is
    // invisible to the first check — a fork's collection is born with its overrides, so it has never
    // been "edited" — and it is only news for a community that already has reports to compare against.
    const hasHistory = reports.some((report) => Boolean(report.completedAt));
    if (!hasHistory && reports.some(isRunning))
        return { kind: "firstRun" };
    const pending = (_a = pendingNextRunAt({ nextRunAt, subtopicsUpdatedAt, lastRunAt, templateActive, autoReportEnabled })) !== null && _a !== void 0 ? _a : (newTemplateNextRunAt && hasHistory && autoReportEnabled === true ? newTemplateNextRunAt : undefined);
    // A run in flight is the pending state answering itself: naming a future date while the report
    // is being built reads as a longer wait than there is.
    if (pending)
        return reports.some(isRunning) ? { kind: "running" } : { kind: "pending", nextRunAt: pending };
    const savedAt = time(subtopicsUpdatedAt);
    if (savedAt === undefined)
        return undefined;
    // Every report from the run that picked up the edit onwards was built with those subtopics, so
    // each of them says so. The notice answers a question the reader has about the report in front
    // of them — "does this include what I changed?" — and that answer does not expire.
    const selected = reports.find((report) => report.reportId === selectedReportId);
    const finishedAt = time(selected === null || selected === void 0 ? void 0 : selected.completedAt);
    if (finishedAt === undefined || finishedAt < savedAt)
        return undefined;
    // A change is only a change against a report that came before it. Subtopics chosen while setting
    // up a community's first report describe nothing the reader saw the other side of.
    const reportBeforeTheChange = reports.some((report) => { var _a; return ((_a = time(report.completedAt)) !== null && _a !== void 0 ? _a : Infinity) < savedAt; });
    if (!reportBeforeTheChange)
        return undefined;
    return { kind: "applied", editedAt: subtopicsUpdatedAt, ranAt: selected === null || selected === void 0 ? void 0 : selected.completedAt };
};
/**
 * When the community's next report is scheduled and the template that will produce it has never
 * run. Read from the community's templates rather than from the report on screen: a template
 * created in Report Settings has no reports of its own yet, so nothing would ever point at it.
 */
export const newTemplateRunAt = (templates) => {
    var _a;
    const active = (templates !== null && templates !== void 0 ? templates : []).filter((template) => template.isActive);
    const newest = [...active].sort((a, b) => { var _a, _b; return new Date((_a = b.createdAt) !== null && _a !== void 0 ? _a : 0).getTime() - new Date((_b = a.createdAt) !== null && _b !== void 0 ? _b : 0).getTime(); })[0];
    return newest && !newest.lastAssessmentAt ? ((_a = newest.nextReportAt) !== null && _a !== void 0 ? _a : undefined) : undefined;
};
